import { useRef } from 'react';

import { getRoutesDirections } from '@/data/libraries/RoutesTranslated'
import { useLocation, useNavigate, type NavigateFunction, Location, NavLink } from 'react-router-dom';
import { Col, Row, Image } from 'react-bootstrap'

import { useAppSelector } from '@/ui/store/helperRedux'
import { RootState } from '@/ui/store/store'
import { ICart } from '@/domain/interfaces/ICart'

import { getCSSVariableValue } from '@/domain/utils/CssExtractVariable';
import { translate } from '@/domain/utils/translate';
import { blurButtonAfterClick } from '@/domain/utils/blurButtonAfterClick';

import TwoButtonsGroup from '@/ui/components/TwoButtonsGroup'
import CartWidget from '@/ui/components/sales/CartWidget'
import SearchInput from '@/ui/components/store/SearchInput'

import LoginMenu from "@/ui/containers/Header/LoginMenu"
import Menu from "@/ui/containers/Header/Menu"

import { IStore } from '@/domain/interfaces/IStore';
import { Layouts } from '@/data/libraries/Layouts';



interface DefaultHeaderProps {
    /** Logo de la aplicación */
    logo: string;
    /** Maneja renderizado de login */
    handleShowLogin: () => void;
    /** Maneja renderizado de Registro  */
    handleShowRegister: () => void;
    /** Maneja renderizado de carrito  */
    handleShowCartModal: () => void;
    /** Maneja renderizado opciones de usuario  */
    handleShowOptionsUser: () => void;
    /** Envía a Mis compras */
    goToPurchases: () => void;
}

/**
 * Componente versión DEFAULT
 * @component
 */
const DefaultHeader = ({
    logo,
    handleShowLogin,
    handleShowRegister,
    handleShowCartModal,
    handleShowOptionsUser,
    goToPurchases,
}: DefaultHeaderProps): JSX.Element => {

    /** Hook para obtener el estado de la tienda. */
    const cartProducts = useAppSelector((state: RootState) => state.cartState) as ICart;
    /** Hook para obtener el estado del usuario logueado. */
    const userLogued = useAppSelector((state) => state.userState);
    /** Hook para generar una navegación dentro de la aplicación. */
    const navigate: NavigateFunction = useNavigate();
    /** Hook para obtener la ubicación actual de la aplicación. */
    const location: Location = useLocation();

    const buttonVariant = getCSSVariableValue('--link-button-variant-header');
    const iconVariant = getCSSVariableValue('--icon-variant-light');

    /** Estado Redux Tienda */
    const storeData = useAppSelector((state: RootState) => state.storeData) as IStore | null;

    /** Rutas URLs */
    const routesDirections = getRoutesDirections();

    /** Referencia al boton de mis compras */
    const buttonRef = useRef<HTMLButtonElement>(null);

    /** Referencia al boton del carrito */
    const cartButtonRef = useRef<HTMLButtonElement>(null);

    /** Función de click del botón mis compras */
    const handleClickMyPurchases = () => blurButtonAfterClick(buttonRef, goToPurchases);

    /** Función de click del botón carrito */
    const handleCartClick = () => blurButtonAfterClick(cartButtonRef, handleShowCartModal);

    /** 
     * Implementación manual de la misma lógica de blurButtonAfterClick
     * para los botones de login y registro 
     */
    const handleLoginClick = () => {
        handleShowLogin();
        // Implementación equivalente a la lógica de blurButtonAfterClick
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement instanceof HTMLButtonElement) {
            activeElement.blur();
        }
    };

    const handleRegisterClick = () => {
        handleShowRegister();
        // Implementación equivalente a la lógica de blurButtonAfterClick
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement instanceof HTMLButtonElement) {
            activeElement.blur();
        }
    };

    return (
        <Row className='g-0'>
            <Col className='d-flex align-items-center py-3 '>
                <Col lg={1} className='px-1'>
                    <NavLink to={routesDirections.MAIN_ROUTE} state={{ originURL: location.pathname }}>
                        <Image
                            id='logo'
                            src={logo}
                            alt='logo'
                            className={`${storeData?.layoutCode === Layouts.DEFAULT ? 'logo-header-default' : 'logo-header'}`}
                            onClick={() => navigate(routesDirections.MAIN_ROUTE, { state: { originURL: location.pathname } })}
                        />
                    </NavLink>
                </Col>
                <Col lg={3} className='px-1 '>
                    <SearchInput />
                </Col>
                <Col lg={3} className='d-flex justify-content-end align-items-center px-1'>
                    <Menu />
                </Col>
                <Col lg={1} className='d-flex justify-content-center align-items-center px-1'>
                    <button
                        ref={buttonRef}
                        className={`${buttonVariant} text-center link-text-standard list-inline-item`}
                        onClick={handleClickMyPurchases}
                    >
                        <span className='paragraph-category-text'>{translate("default_header.purchases_title")}</span>
                    </button>
                </Col>
                <Col lg={1} className='ps-5'>
                    <button
                        ref={cartButtonRef}
                        className={`${buttonVariant} ${iconVariant} cursor-hand btn border-0`}
                        onClick={handleCartClick}
                    >
                        <CartWidget />
                    </button>
                </Col>
                {userLogued?.user.isLogued ? (
                    <Col lg={3} className='d-flex justify-content-start align-items-center px-1'>
                        <LoginMenu handleShowOptionsUser={handleShowOptionsUser} />
                    </Col>
                ) : (
                    <Col lg={3} className='d-flex justify-content-start align-items-center px-1'>
                        <TwoButtonsGroup
                            firstButtonClass={`btn-secondary-icon-small ${cartProducts.reservationId ? 'disabled' : ''}`}
                            firstButtonText={translate("default_header.login_label")}
                            firstButtonClick={handleLoginClick}
                            secondButtonClass={`btn-primary-icon-small ${cartProducts.reservationId ? 'disabled' : ''}`}
                            secondButtonText={translate("default_header.sign_up_label")}
                            secondButtonClick={handleRegisterClick}
                        />
                    </Col>
                )}
            </Col>
        </Row>
    )
}

export default DefaultHeader